// What a marketing visitor arrived with, kept in memory ONLY (no cookies, no
// storage) so it survives Inertia navigation between marketing pages:
//   - `from`: where they clicked a tenant site's "Made with TOBY" badge
//     (`/?from=<page URL>&utm_source=…`);
//   - `ref`: a customer's referral code (`/pricing?ref=GOURMET`, or typed into
//     the pricing page's "Empfehlungscode" field).
// Both are stripped from the address bar once captured. They reach the server
// as a `return_to` on the sign-in link, and the account at signup.
import { homePath } from "@/lib/locale-routes"

export type SiteShortcut = { site_name: string; host: string; edit_url: string }

export const landingReferral = $state<{
  from: string | null
  ref: string | null
  shortcut: SiteShortcut | null
}>({ from: null, ref: null, shortcut: null })

// Stripped together with `from` (the badge link's tracking params).
const FROM_PARAMS = ["from", "utm_source", "utm_medium"]

export function captureLandingReferral(
  url: URL,
  shortcut: SiteShortcut | null,
): void {
  if (typeof window === "undefined") return
  const hasFrom = url.searchParams.has("from")
  const hasRef = url.searchParams.has("ref")

  if (hasFrom) {
    landingReferral.from = url.searchParams.get("from")
    landingReferral.shortcut = shortcut
  } else if (shortcut) {
    landingReferral.shortcut = shortcut
  }
  if (hasRef) landingReferral.ref = url.searchParams.get("ref")?.trim() || null

  if (hasFrom || hasRef) {
    const clean = new URL(url)
    if (hasFrom) for (const key of FROM_PARAMS) clean.searchParams.delete(key)
    if (hasRef) clean.searchParams.delete("ref")
    // Keep history.state: Inertia stores its page object there.
    window.history.replaceState(
      window.history.state,
      "",
      clean.pathname + clean.search + clean.hash,
    )
  }
}

/** True once the visitor carries a badge `from` or a referral `ref`. */
export function hasLandingReferral(): boolean {
  return Boolean(landingReferral.from || landingReferral.ref)
}

/** Append `return_to` (this locale's landing page + the stored `from` and/or
 *  `ref`) to a sign-in href, merging with any params it already has. No-op
 *  without either. */
export function withReturnTo(href: string, localeCode: string): string {
  if (!hasLandingReferral()) return href
  const [path, query = ""] = href.split("?")
  const params = new URLSearchParams(query)
  const landing = new URLSearchParams()
  if (landingReferral.from) landing.set("from", landingReferral.from)
  if (landingReferral.ref) landing.set("ref", landingReferral.ref)
  params.set("return_to", `${homePath(localeCode)}?${landing}`)
  return `${path}?${params}`
}
